import React, { createContext, type FC, type PropsWithChildren, useCallback, useMemo, useRef } from 'react';

// Resolves `true` if it's OK for the guarded action to proceed, `false` to abort it entirely
// (e.g. the user dismissed a confirmation dialog without making a choice).
export type NavigationGuard = () => Promise<boolean>;

interface NavigationGuardContextProps {
    registerGuard: (guard: NavigationGuard) => () => void;
    guardAction: (action: () => void | Promise<void>) => Promise<void>;
}

// Runs `action` synchronously when there is nothing to guard against - this must stay
// synchronous (no `await` before it) so callers that fire-and-forget a guarded action
// see its side effects take place immediately, exactly as if it were called directly.
const runUnguarded = (action: () => void | Promise<void>): Promise<void> => Promise.resolve(action());

const NavigationGuardContext = createContext<NavigationGuardContextProps>({
    registerGuard: () => () => {},
    guardAction: runUnguarded
});

NavigationGuardContext.displayName = 'NavigationGuardContext';

const NavigationGuardProvider: FC<PropsWithChildren> = ({ children }) => {
    const guardRef = useRef<NavigationGuard | null>(null);

    const registerGuard = useCallback((guard: NavigationGuard) => {
        guardRef.current = guard;
        return () => {
            if (guardRef.current === guard) {
                guardRef.current = null;
            }
        };
    }, []);

    const guardAction = useCallback((action: () => void | Promise<void>): Promise<void> => {
        const guard = guardRef.current;
        return guard
            ? guard().then((shouldProceed) => (shouldProceed ? runUnguarded(action) : undefined))
            : runUnguarded(action);
    }, []);

    const value = useMemo(() => ({ registerGuard, guardAction }), [registerGuard, guardAction]);

    return (
        <NavigationGuardContext.Provider value={value}>
            {children}
        </NavigationGuardContext.Provider>
    );
};

const useNavigationGuard = () => React.useContext(NavigationGuardContext);

export { NavigationGuardProvider, useNavigationGuard, NavigationGuardContext };
